import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import Api from "../api/api"
import { useSku } from "../redux/skusSlice"
import { License, LicenseOrigin, LicenseType, Product, licenseOriginLabels, licenseTypeLabels } from "../api/models"
import Spinner from "./widgets/Spinner"
import { formatExpiryDate, isLocked } from "./widgets/licenseCells"

// the sku the license came through (resolved server-side via its
// entitlement); absent for trial licenses
type UserLicense = License & { skuId?: string }

// exported for the product page's Licenses/Trials cards
export function formatDate(d?: string | null) {
  if (!d) return null
  return new Date(d).toLocaleDateString()
}

// grant/reward refs have no detail pages — those origins render as text
const originDetailPath: Partial<Record<LicenseOrigin, (refId: string) => string>> = {
  [LicenseOrigin.Order]: (refId) => `/orders/${refId}`,
  [LicenseOrigin.Code]: (refId) => `/redemptions/${refId}`,
  [LicenseOrigin.Subscription]: (refId) => `/subscriptions/${refId}`,
}

// resolves the sku name via the cached skus slice; falls back to the raw id.
// Split in two so useSku (which fetches on mount) only runs for a real id —
// trial licenses have no sku.
function SkuLink({ skuId }: { skuId: string }) {
  const { data: sku } = useSku(skuId)
  return <Link to={`/skus/${skuId}`} className="card-link">{sku?.name ?? skuId}</Link>
}

export function SkuCell({ skuId }: { skuId?: string }) {
  return <td title={skuId}>{skuId ? <SkuLink skuId={skuId} /> : "—"}</td>
}

export function OriginCell({ license }: { license: License }) {
  const pathFor = originDetailPath[license.origin]
  const label = licenseOriginLabels[license.origin]
  return (
    <td title={license.refId}>
      {pathFor && license.refId ? (
        <Link to={pathFor(license.refId)} className="card-link">{label}</Link>
      ) : (
        label
      )}
      {license.type !== LicenseType.Standard && (
        <span style={{ color: "var(--nfa-light-grey)", fontSize: "9pt", marginLeft: 6 }}>
          {licenseTypeLabels[license.type]}
        </span>
      )}
    </td>
  )
}

// One user's deposited licenses for the admin user page. State is local for
// the same reason as EntitlementsTable: the licenses redux slice is a
// singleton keyed to the signed-in user. No pagination — the backend returns
// the full (small) set at once.
export default function UserLicensesTable({ userId, accessToken }: {
  userId: string
  accessToken: string
}) {
  const [licenses, setLicenses] = useState<UserLicense[] | undefined>(undefined)
  const [productsById, setProductsById] = useState<Record<string, Product>>({})
  const [error, setError] = useState(false)
  // stale responses (user changed mid-flight) must not land in state
  const requestSeq = useRef(0)

  useEffect(() => {
    const seq = ++requestSeq.current
    setLicenses(undefined)
    setError(false)
    Api.adminGetUserLicenses({ userId, accessToken })
      .then(({ licenses, products }) => {
        if (seq !== requestSeq.current) return
        setLicenses(licenses)
        setProductsById(Object.fromEntries(products.map((product) => [product.id, product])))
      })
      .catch(() => {
        if (seq === requestSeq.current) setError(true)
      })
  }, [userId, accessToken])

  if (error) {
    return <p className="empty-state">Error loading licenses</p>
  }

  if (licenses === undefined) {
    return <p className="empty-state"><Spinner size={16} /> Loading licenses…</p>
  }

  // headerless empty state — a lone header row over "no results" is just noise
  if (licenses.length === 0) {
    return <p className="empty-state">None</p>
  }

  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>SKU</th>
            <th>Origin</th>
            <th>Created</th>
            <th>Expires</th>
            <th>Activations</th>
          </tr>
        </thead>
        <tbody>
          {licenses.map((license) => (
            <tr key={license.id} className={license.expired ? "row-muted" : undefined}>
              <td title={license.productId}>
                <Link to={`/products/${license.productId}`} className="card-link">
                  {productsById[license.productId]?.name ?? license.productId}
                </Link>
                {isLocked(license) && <span className="badge-locked">Locked</span>}
              </td>
              <SkuCell skuId={license.skuId} />
              <OriginCell license={license} />
              <td>{formatDate(license.createdAt)}</td>
              <td>
                {formatExpiryDate(license)}
                {license.expired && (
                  <span className="status-badge status-expired" style={{ marginLeft: 6 }}>expired</span>
                )}
              </td>
              <td>{license.activationIds.active.length} / {license.maxActivations}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
